import {
  DatePicker,
  DefaultButton,
  Dropdown,
  IDropdownOption,
  PrimaryButton,
  Stack,
  TextField,
  useTheme
} from "@bps/fluent-ui";

export interface CommandAuditFilterFormValues {
  fieldDeviceId: string;
  correlationId: string;
  softwarePackageCode: string;
  commandName: string;
  startDateTime?: Date;
  endDateTime?: Date;
  responseCode?: number;
  sourceType?: string;
}

interface CommandAuditFiltersProps {
  filterValues: CommandAuditFilterFormValues;
  setFilterValues: React.Dispatch<
    React.SetStateAction<CommandAuditFilterFormValues>
  >;
  onSearch: () => void;
  onRefresh: () => void;
  canRefresh: boolean;
  isRefreshing: boolean;
  displayFieldDeviceIdFilter?: boolean;
}

export const initialCommandAuditFilterValues = (
  fieldDeviceId: string
): CommandAuditFilterFormValues => ({
  fieldDeviceId: fieldDeviceId ?? "",
  correlationId: "",
  softwarePackageCode: "",
  commandName: "",
  startDateTime: undefined,
  endDateTime: undefined,
  responseCode: undefined,
  sourceType: undefined
});

const responseCodeOptions: IDropdownOption[] = [
  { key: 200, text: "200" },
  { key: 202, text: "202" },
  { key: 204, text: "204" },
  { key: 400, text: "400" },
  { key: 401, text: "401" },
  { key: 403, text: "403" },
  { key: 404, text: "404" },
  { key: 500, text: "500" }
];

const sourceTypeOptions: IDropdownOption[] = [
  { key: "DeviceInitiated", text: "DeviceInitiated" },
  { key: "Realtime", text: "Realtime" },
  { key: "Queued", text: "Queued" }
];

const makeDatePickerTextFieldProps = (label: string) => ({
  label,
  styles: {
    root: { width: "100%" },
    wrapper: { width: "100%" },
    fieldGroup: { width: "100%" },
    field: { width: "100%" }
  }
});

export const CommandAuditFilters: React.FC<CommandAuditFiltersProps> = ({
  filterValues,
  setFilterValues,
  onSearch,
  onRefresh,
  canRefresh,
  isRefreshing,
  displayFieldDeviceIdFilter = true
}) => {
  const theme = useTheme();

  return (
    <>
      <Stack horizontal wrap tokens={{ childrenGap: theme.spacing.m }}>
        {displayFieldDeviceIdFilter && (
          <Stack styles={{ root: { minWidth: 240 } }}>
            <TextField
              label="FieldDeviceId"
              value={filterValues.fieldDeviceId}
              onChange={(_, value): void => {
                setFilterValues(prev => ({
                  ...prev,
                  fieldDeviceId: value ?? ""
                }));
              }}
            />
          </Stack>
        )}

        <Stack styles={{ root: { minWidth: 240 } }}>
          <TextField
            label="CorrelationId"
            value={filterValues.correlationId}
            onChange={(_, value): void => {
              setFilterValues(prev => ({
                ...prev,
                correlationId: value ?? ""
              }));
            }}
          />
        </Stack>

        <Stack styles={{ root: { minWidth: 240 } }}>
          <TextField
            label="SoftwarePackageCode"
            value={filterValues.softwarePackageCode}
            onChange={(_, value): void => {
              setFilterValues(prev => ({
                ...prev,
                softwarePackageCode: value ?? ""
              }));
            }}
          />
        </Stack>

        <Stack styles={{ root: { minWidth: 240 } }}>
          <TextField
            label="CommandName"
            value={filterValues.commandName}
            onChange={(_, value): void => {
              setFilterValues(prev => ({
                ...prev,
                commandName: value ?? ""
              }));
            }}
          />
        </Stack>

        <Stack styles={{ root: { minWidth: 240 } }}>
          <Dropdown
            name="responseCode"
            label="ResponseCode"
            selectedKey={filterValues.responseCode ?? ""}
            options={responseCodeOptions}
            onChange={(_, options): void => {
              const selectedOption = options.option;

              setFilterValues(prev => ({
                ...prev,
                responseCode:
                  selectedOption?.key === "" || selectedOption?.key == null
                    ? undefined
                    : Number(selectedOption.key)
              }));
            }}
          />
        </Stack>

        <Stack styles={{ root: { minWidth: 240 } }}>
          <Dropdown
            name="sourceType"
            label="SourceType"
            selectedKey={filterValues.sourceType ?? ""}
            options={sourceTypeOptions}
            onChange={(_, options): void => {
              const selectedOption = options.option;

              setFilterValues(prev => ({
                ...prev,
                sourceType:
                  selectedOption?.key === "" || selectedOption?.key == null
                    ? undefined
                    : String(selectedOption.key)
              }));
            }}
          />
        </Stack>
      </Stack>

      <Stack styles={{ root: { width: 240 } }}>
        <DatePicker
          adjustWidthAsPlaceholder={false}
          textFieldProps={makeDatePickerTextFieldProps("StartDateTime")}
          value={filterValues.startDateTime}
          onChange={(value): void => {
            setFilterValues(prev => ({
              ...prev,
              startDateTime: value ?? undefined
            }));
          }}
        />
      </Stack>

      <Stack styles={{ root: { width: 240 } }}>
        <DatePicker
          adjustWidthAsPlaceholder={false}
          textFieldProps={makeDatePickerTextFieldProps("EndDateTime")}
          value={filterValues.endDateTime}
          onChange={(value): void => {
            setFilterValues(prev => ({
              ...prev,
              endDateTime: value ?? undefined
            }));
          }}
        />
      </Stack>

      <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 8 }}>
        <DefaultButton
          iconProps={{ iconName: isRefreshing ? "Sync" : "Refresh" }}
          disabled={!canRefresh || isRefreshing}
          onClick={onRefresh}
        >
          {isRefreshing ? "Refreshing..." : "Refresh"}
        </DefaultButton>
        <PrimaryButton onClick={onSearch}>Search</PrimaryButton>
      </Stack>
    </>
  );
};
